import type { KeyName } from "@/lib/components/ui/keyboard-shortcut/keyboard-shortcut";
import { useSearchParams } from "react-router";

/** LIBRARY_PARAM is the search param the library view rides on. */
export const LIBRARY_PARAM = "library";

/** MODULE_PARAM names the module whose settings the workspace is showing. */
export const MODULE_PARAM = "moduleId";

/** MODULE_TAB_PARAM is the open tab within a module's settings. */
export const MODULE_TAB_PARAM = "moduleTab";

/**
 * OVERVIEW is the tab a module's settings open on. It is not a tab of its own,
 * it links into the others, and it answers to "general" so older links work.
 */
export const OVERVIEW = "general";

/** LIBRARY_KEYS opens the library from anywhere in the app. */
export const LIBRARY_KEYS: readonly KeyName[] = ["Meta", "K"];

/**
 * useModuleLibrary drives the module workspace through the url. The workspace
 * is one dialog with two views: the library while only LIBRARY_PARAM is set, a
 * module's settings once MODULE_PARAM is. Moving between them swaps params
 * rather than closing anything, so the frame stays put and only its contents
 * change. The rest of the url is left alone, so the page behind the workspace
 * keeps its assistant and its thread.
 */
export function useModuleLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();

  return {
    isOpen: searchParams.has(LIBRARY_PARAM) || searchParams.has(MODULE_PARAM),
    isCatalogueOpen: searchParams.has(LIBRARY_PARAM),
    // also the way back out of a module's settings, from ⌘K or the rail
    open: () =>
      setSearchParams((prev) => {
        prev.delete(MODULE_PARAM);
        prev.delete(MODULE_TAB_PARAM);
        prev.set(LIBRARY_PARAM, "1");
        return prev;
      }),
    close: () =>
      setSearchParams((prev) => {
        prev.delete(LIBRARY_PARAM);
        prev.delete(MODULE_PARAM);
        prev.delete(MODULE_TAB_PARAM);
        return prev;
      }),
    openSettings: (moduleId: string) =>
      setSearchParams((prev) => {
        prev.delete(LIBRARY_PARAM);
        prev.set(MODULE_PARAM, moduleId);
        prev.set(MODULE_TAB_PARAM, OVERVIEW);
        return prev;
      }),
  };
}
